import { useEffect, useState } from 'react';
import {
    Modal,
    Table,
    Typography,
    Button,
    Alert,
    Popconfirm,
    Tooltip,
    Spin,
} from 'antd';
import { observer } from 'mobx-react';
import style from './style.module.scss';
import { useClientStore } from '../../../../../contexts/mobx';
import { PaginationBar } from '../../../../../components/pagination/pagination-bar';
import { Tag } from '../../../../../components/tag';
import {
    CheckCircleOutlined,
    WarningOutlined,
    PhoneOutlined,
    ClockCircleOutlined,
    MessageOutlined,
} from '@ant-design/icons';
import SMSDeliveryModal from '../SMSDeliveryModal';
import { PhoneValidationCode } from '../../../../../stores/client';

const { Text, Title } = Typography;

interface PhoneValidationModalProps {
    isVisible: boolean;
    onClose: () => void;
    clientId?: number;
    phoneNumber?: string;
    isPhoneConfirmed?: boolean;
}

// Page size fixed to 3 for this page because table is displayed in a modal and we want to limit its height.
const PAGE_SIZE = 3;

export const PhoneValidationModal = ({
    isVisible,
    onClose,
    clientId,
    phoneNumber,
    isPhoneConfirmed,
}: PhoneValidationModalProps) => {
    const clientStore = useClientStore();
    const [showSMSDeliveryModal, setShowSMSDeliveryModal] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState<
        string | undefined
    >();
    const [validationSuccess, setValidationSuccess] = useState(false);
    const [localPhoneConfirmed, setLocalPhoneConfirmed] = useState(
        isPhoneConfirmed
    );
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        if (isVisible && clientId && phoneNumber) {
            // API uses offset, so convert page to offset
            const offset = currentPage * PAGE_SIZE;
            clientStore.fetchPhoneValidationCodes(clientId, PAGE_SIZE, offset);
            clientStore.fetchPhoneValidatedUsers(clientId);
            setValidationSuccess(false);
            setLocalPhoneConfirmed(isPhoneConfirmed);
        }
    }, [
        isVisible,
        clientId,
        clientStore,
        isPhoneConfirmed,
        currentPage,
        phoneNumber,
    ]);

    const formatTime = (dateString: string): string => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        }).format(date);
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(date);
    };

    const handleViewSMSDelivery = (messageId?: string) => {
        if (messageId) {
            setSelectedMessageId(messageId);
            setShowSMSDeliveryModal(true);
        }
    };

    const handleValidatePhone = async () => {
        if (clientId) {
            const result = await clientStore.validatePhoneNumber(clientId);
            if (result) {
                setValidationSuccess(true);
                setLocalPhoneConfirmed(true);
            }
        }
    };

    const otherValidatedUsers = clientStore.phoneValidatedUsers.filter(
        (user) => user.id !== clientId
    );

    const columns = [
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a: PhoneValidationCode, b: PhoneValidationCode) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime(),
            render: (createdAt: string) => (
                <div>
                    <Text strong style={{ fontSize: '14px' }}>
                        {formatTime(createdAt)}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {formatDate(createdAt)}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Code',
            dataIndex: 'confirmationCode',
            key: 'confirmationCode',
        },
        {
            title: 'Client ID',
            dataIndex: 'clientId',
            key: 'clientId',
        },
        {
            title: 'Details',
            key: 'action',
            width: 100,
            render: (_: unknown, record: PhoneValidationCode) => (
                <Tooltip title="View SMS delivery details">
                    <Button
                        icon={<MessageOutlined />}
                        className={style.deliveryButton}
                        onClick={() => handleViewSMSDelivery(record.messageId)}
                        disabled={!record.messageId}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <Modal
            title={
                <div className={style.modalHeader}>
                    <PhoneOutlined className={style.phoneIcon} />
                    <Title level={4} className={style.modalTitle}>
                        Phone Validation
                    </Title>
                </div>
            }
            open={isVisible}
            onCancel={() => {
                onClose();
            }}
            footer={null}
            centered
            width={800}
            className={style.phoneValidationModal}
        >
            <div className={style.phoneInfoSection}>
                <div className={style.phoneNumberDisplay}>
                    <Text strong>Phone Number: </Text>
                    <Text>{phoneNumber || 'Not provided'}</Text>
                    {localPhoneConfirmed ? (
                        <Tag
                            variant="success"
                            icon={<CheckCircleOutlined />}
                            className={style.statusTag}
                        >
                            Validated
                        </Tag>
                    ) : (
                        <Tag
                            variant="orange"
                            icon={<ClockCircleOutlined />}
                            className={style.statusTag}
                        >
                            Pending Validation
                        </Tag>
                    )}
                </div>

                {validationSuccess && (
                    <Alert
                        message="Success"
                        description="Phone number has been successfully validated."
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined />}
                        className={style.successAlert}
                    />
                )}

                {otherValidatedUsers.length > 0 ? (
                    <Alert
                        message="Warning"
                        description={`${otherValidatedUsers.length} other account(s) have the same phone number validated.`}
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        className={style.alertMessage}
                    />
                ) : (
                    <Alert
                        message="No Conflicts"
                        description="No other accounts have this phone number validated."
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined />}
                        className={style.alertMessage}
                    />
                )}

                {!localPhoneConfirmed && (
                    <div className={style.actionSection}>
                        <Popconfirm
                            title="Are you sure you want to force validate this phone number?"
                            onConfirm={handleValidatePhone}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                loading={clientStore.isValidatingPhoneNumber}
                                disabled={validationSuccess}
                            >
                                Force Validate Phone Number
                            </Button>
                        </Popconfirm>
                    </div>
                )}
            </div>

            <Title level={4} className={style.tableTitle}>
                SMS Verification Codes
            </Title>

            {clientStore.isFetchingPhoneValidationCodes ? (
                <div className={style.loadingContainer}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    {clientStore.phoneValidationCodes &&
                        clientStore.phoneValidationCodes.total > 0 && (
                            <div>
                                <PaginationBar
                                    pagination={{
                                        page: currentPage,
                                        pageSize: PAGE_SIZE,
                                        total:
                                            clientStore.phoneValidationCodes
                                                .total,
                                        onPageChange: (page) =>
                                            setCurrentPage(page),
                                    }}
                                />
                            </div>
                        )}
                    <Table
                        columns={columns}
                        dataSource={
                            clientStore.phoneValidationCodes?.results || []
                        }
                        rowKey="id"
                        pagination={false}
                        className={style.codesTable}
                    />
                </>
            )}

            <SMSDeliveryModal
                isVisible={showSMSDeliveryModal}
                onClose={() => setShowSMSDeliveryModal(false)}
                messageId={selectedMessageId}
            />
        </Modal>
    );
};

export default observer(PhoneValidationModal);
